import { IconDeviceMobile, IconX } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useSession } from "../session";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISSED = "installDismissed";

/** Shown on Home when the browser offers installation and the customer has not dismissed it on this device. */
export function InstallBanner() {
  const { t } = useSession();
  const [prompt, setPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [hidden, setHidden] = useState(() => {
    try {
      return localStorage.getItem(DISMISSED) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setPrompt(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => setPrompt(null);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (!prompt || hidden) return null;

  const dismiss = () => {
    setHidden(true);
    try {
      localStorage.setItem(DISMISSED, "1");
    } catch {
      // ignore
    }
  };

  const install = async () => {
    await prompt.prompt();
    const choice = await prompt.userChoice.catch(() => ({ outcome: "dismissed" as const }));
    if (choice.outcome === "accepted") setPrompt(null);
    else dismiss();
  };

  return (
    <div className="install-banner" role="region" aria-label={t("installTitle")}>
      <IconDeviceMobile size={28} stroke={1.5} className="install-icon" />
      <div className="install-text">
        <strong>{t("installTitle")}</strong>
        <span className="muted">{t("installBody")}</span>
      </div>
      <div className="install-actions">
        <button type="button" className="button button-primary button-small" onClick={() => void install()}>
          {t("install")}
        </button>
        <button type="button" className="icon-button" aria-label={t("notNow")} onClick={dismiss}>
          <IconX size={18} />
        </button>
      </div>
    </div>
  );
}
